// =============================================================================
// ResetPasswordScreen — landing page for /reset-password/:token
// =============================================================================
// Public route. Validates the token on mount (so we can show the
// recipient's username before asking for a password), then accepts a new
// password. On success, surfaces a "go to login" link so the user can
// sign in with the new password right away.
//
// Failure modes (token unknown, expired, already-used) collapse to a
// single error message — we don't differentiate between them to a
// would-be brute forcer.
// =============================================================================

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, ApiError } from '../api';
import { Button, Input, Window, Spinner } from '../components/ui';

type Status =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'ready'; username: string; expiresAt: string }
  | { kind: 'submitting'; username: string }
  | { kind: 'done'; username: string }
  | { kind: 'error'; username: string; message: string };

export function ResetPasswordScreen() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    api
      .resetPasswordPreview(token)
      .then((res) => {
        if (cancelled) return;
        setStatus({ kind: 'ready', username: res.username, expiresAt: res.expiresAt });
      })
      .catch(() => {
        if (cancelled) return;
        setStatus({ kind: 'invalid' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (status.kind !== 'ready') return;
    if (password.length < 8) {
      setStatus({
        kind: 'error',
        username: status.username,
        message: 'Password must be at least 8 characters.',
      });
      return;
    }
    if (password !== confirm) {
      setStatus({
        kind: 'error',
        username: status.username,
        message: "Passwords don't match.",
      });
      return;
    }
    setStatus({ kind: 'submitting', username: status.username });
    try {
      await api.resetPassword({ token: token!, newPassword: password });
      setStatus({ kind: 'done', username: status.username });
    } catch (err) {
      setStatus({
        kind: 'error',
        username: status.username,
        message: err instanceof ApiError ? err.message : 'Failed to reset password',
      });
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center p-6 bg-bg">
      <div className="w-full max-w-[380px]">
        <Window title="Reset password">
          {status.kind === 'loading' && (
            <div className="flex items-center justify-center p-6">
              <Spinner size={18} />
            </div>
          )}
          {status.kind === 'invalid' && (
            <div className="p-2 space-y-3">
              <p className="text-sm text-text">
                This reset link is invalid, expired, or has already been used.
              </p>
              <p className="text-xs text-text-muted">
                Ask an admin for a new link.
              </p>
              <Button onClick={() => navigate('/')} fullWidth>
                Back to sign in
              </Button>
            </div>
          )}
          {(status.kind === 'ready' ||
            status.kind === 'submitting' ||
            status.kind === 'error') && (
            <form onSubmit={submit} className="space-y-3">
              <p className="text-xs text-text-muted">
                Resetting password for{' '}
                <strong className="text-text">@{status.username}</strong>
              </p>
              <Input
                label="New password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
                minLength={8}
                autoComplete="new-password"
              />
              <Input
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
              {status.kind === 'error' && (
                <div className="text-xs text-danger bg-danger/10 px-2 py-1 rounded-sm">
                  {status.message}
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={status.kind === 'submitting'}
              >
                {status.kind === 'submitting' ? <Spinner size={14} /> : 'Set new password'}
              </Button>
            </form>
          )}
          {status.kind === 'done' && (
            <div className="p-2 space-y-3 text-center">
              <div className="text-2xl">✓</div>
              <p className="text-sm text-text">
                Password updated for{' '}
                <strong>@{status.username}</strong>.
              </p>
              <Button onClick={() => navigate('/')} fullWidth variant="primary">
                Sign in
              </Button>
            </div>
          )}
        </Window>
      </div>
    </div>
  );
}
